import { Link } from 'react-router-dom';
import { Brain, Clock, Sliders } from 'lucide-react';
import { LandingPage, LandingCTA } from './LandingShell';

const costLines = [
  { label: 'Infrastructure', amount: '$0.80' },
  { label: 'AI (50 classifications)', amount: '$0.06' },
  { label: 'Email digests', amount: '$0.02' },
  { label: 'Our margin', amount: '$0.72' },
  { label: "Stripe's cut", amount: '$0.40' },
];

const features = [
  {
    icon: Brain,
    title: 'AI Classification',
    description:
      "Danny reads 'Prepare Q2 board deck for Friday' and knows it's Work > Presentations > High Priority > ~3 hours. You didn't configure that.",
  },
  {
    icon: Clock,
    title: 'Effort Estimation',
    description:
      'Stop guessing. Danny estimates based on task complexity, not your optimism.',
  },
  {
    icon: Sliders,
    title: 'Progressive Control',
    description:
      "Danny suggests. You decide. Turn on more autonomy when you trust it. Turn it off when you don't.",
  },
];

export function LandingPageA() {
  return (
    <LandingPage>
      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-24 pb-20 text-center">
        <span className="label-mono inline-block text-primary-500 border border-primary-500/40 rounded-full px-3 py-1 text-xs mb-6">
          cost-plus pricing
        </span>
        <p className="text-4xl font-semibold text-text-secondary-light dark:text-text-secondary-dark mb-2">
          AI task management.
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark mb-5">
          $2/month. No bullshit.
        </h1>
        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-xl mx-auto mb-10 leading-relaxed">
          AI task management that costs what it should, not what the
          market will bear.
        </p>
        <div className="flex items-center justify-center gap-4">
          <Link
            to="/register"
            className="bg-primary-700 hover:bg-primary-800 text-surface-base-dark font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            Start for $2
          </Link>
          <Link
            to="/login"
            className="border border-border-subtle-light dark:border-border-subtle-dark hover:border-border-hover-light dark:hover:border-border-hover-dark text-text-primary-light dark:text-text-primary-dark font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            Sign in
          </Link>
        </div>
      </section>

      {/* Cost Breakdown */}
      <section className="max-w-lg mx-auto px-6 pb-20">
        <div className="rounded-2xl bg-surface-raised-dark border border-border-subtle-dark px-8 py-10 sm:px-10 sm:py-12">
          <h2 className="label-mono text-text-secondary-dark text-sm mb-8 tracking-widest text-center">
            YOUR $2/MONTH
          </h2>
          <div className="space-y-3 font-mono text-[15px]">
            {costLines.map((line) => (
              <div
                key={line.label}
                className="flex items-baseline gap-2"
              >
                <span className="text-text-secondary-dark whitespace-nowrap">
                  {line.label}
                </span>
                <span className="flex-1 border-b border-dotted border-border-subtle-dark translate-y-[-4px]" />
                <span className="text-text-primary-dark tabular-nums whitespace-nowrap">
                  {line.amount}
                </span>
              </div>
            ))}
          </div>

          <div className="border-t border-border-subtle-dark my-8" />

          <div className="space-y-2 text-[15px] text-text-secondary-dark font-mono">
            <p>Use more AI? You pay what it costs us, plus a modest margin.</p>
            <p>Power user: $2.58. Extreme user: $9.72.</p>
            <p className="font-bold text-primary-500">Nobody pays $34.</p>
          </div>
        </div>
      </section>

      {/* Cheeky Explainer */}
      <section className="max-w-3xl mx-auto px-6 pb-20">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark mb-5">
          Wait, the AI is actually good?
        </h2>
        <p className="text-text-secondary-light dark:text-text-secondary-dark leading-relaxed mb-4">
          Same models as the $34/month apps. We just don't have 400 employees,
          a sales team, or a Super Bowl ad to pay for.
        </p>
        <p className="text-text-secondary-light dark:text-text-secondary-dark leading-relaxed">
          The other guys charge $34 because they can, not because they need to.
        </p>
      </section>

      {/* Features */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="grid sm:grid-cols-3 gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="rounded-2xl border border-border-subtle-light dark:border-border-subtle-dark bg-surface-raised-light dark:bg-surface-raised-dark p-6"
            >
              <feature.icon className="w-6 h-6 text-primary-500 mb-4" />
              <h3 className="font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison Row */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <p className="font-mono text-sm text-text-tertiary-light dark:text-text-tertiary-dark text-center">
          Todoist Pro: $5/mo · Motion: $34/mo · Sunsama: $20/mo ·{' '}
          <span className="text-primary-500 font-semibold">Danny: $2/mo</span>
        </p>
      </section>

      {/* CTA */}
      <LandingCTA
        headline="$2. What's the worst that could happen?"
        subtitle="Cancel anytime. We're not going to make it weird."
      />
    </LandingPage>
  );
}
